import { Alert, Button, Group, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

const getMessageKey = (isPaused: boolean, isSupported: boolean) => {
	if (!isPaused) {
		return 'documents_page.live_updates_paused.action_timed_out';
	}

	return isSupported
		? 'documents_page.live_updates_paused.reconnecting'
		: 'documents_page.live_updates_paused.unsupported';
};

interface Props {
	/** Derive with `useLiveUpdatesPaused` — `!isConnected` flickers on every normal retry. */
	isPaused: boolean;
	/** False when the browser has no EventSource at all. */
	isSupported: boolean;
	/**
	 * An action's expected change never arrived. Distinct from `isPaused`: the
	 * stream can be healthy and still drop a single signal, in which case this is
	 * the only thing telling the user their view is behind.
	 */
	isActionTimedOut?: boolean;
	/** Refetch everything this screen shows. */
	onRefresh: () => void;
}

export const LiveUpdatesPausedBanner = ({
	isPaused,
	isSupported,
	isActionTimedOut = false,
	onRefresh,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	if (!isPaused && !isActionTimedOut) {
		return null;
	}

	const message = tMipManage(getMessageKey(isPaused, isSupported));

	return (
		<Alert color="yellow" variant="light" p="xs" data-testid="live-updates-paused-banner">
			<Group justify="space-between" wrap="nowrap" gap="sm">
				<Text size="sm">{message}</Text>
				<Button size="compact-sm" variant="subtle" onClick={onRefresh}>
					{tMipManage('documents_page.live_updates_paused.refresh')}
				</Button>
			</Group>
		</Alert>
	);
};
